<!-- /*
========================================================
✅ ResponsiveImage.vue (Reusable Shopify Image Component)
========================================================

PURPOSE:
- Shopify CDN optimized images
- WebP + JPG fallback
- srcset support
- mobile/desktop different images support
- lazy/eager loading
- CLS safe (aspect ratio)

--------------------------------------------------------
📦 REQUIRED PROP
--------------------------------------------------------

img (Object)

Single image structure:
{
  webp_srcset: "...",
  jpg_srcset: "...",
  fallback: "...",
  alt: "...",
  width: 1200,
  height: 1500
}

--------------------------------------------------------
📦 OPTIONAL (mobile + desktop different images)
--------------------------------------------------------

img (Object)

{
  mobile: {
    webp: "...",
    jpg: "..."
  },
  desktop: {
    webp: "...",
    jpg: "..."
  },
  fallback: "...",
  alt: "...",
  width: 1920,
  height: 900
}

--------------------------------------------------------
📦 OPTIONAL PROPS
--------------------------------------------------------

sizes        → "(max-width:768px) 100vw, 33vw"
loading      → "lazy" | "eager"
imgClass     → css class for <img>
wrapperClass → css class for <>
aspectRatio  → "1/1", "4/5", "16/9"
objectFit    → "cover" | "contain"

--------------------------------------------------------
✅ USAGE EXAMPLES
--------------------------------------------------------

# 1️⃣ Normal image
<ResponsiveImage :img="item.previewImg" />

# 2️⃣ With custom class
<ResponsiveImage
  :img="item.previewImg"
  imgClass="image"
/>

# 3️⃣ Square ratio
<ResponsiveImage
  :img="item.previewImg"
  aspectRatio="1/1"
/>

# 4️⃣ Hero banner (full width)
<ResponsiveImage
  :img="heroImage"
  sizes="100vw"
  loading="eager"
/>

# 5️⃣ Mobile + Desktop different images
<ResponsiveImage
  :img="heroResponsiveImage"
/>

--------------------------------------------------------
📌 NOTES
--------------------------------------------------------
- Use eager only for first (LCP) image
- Use lazy for rest
- Always send Shopify image_url with srcset
- Prevent CLS using width/height or aspectRatio

========================================================
*/
 -->
<template>
  <picture :class="wrapperClass">
    <!-- ===== SINGLE IMAGE MODE ===== -->
    <template v-if="img.webp_srcset">
      <source :srcset="img.webp_srcset" type="image/webp" :sizes="sizes" />

      <source :srcset="img.jpg_srcset" type="image/jpeg" :sizes="sizes" />
    </template>

    <!-- ===== MOBILE/DESKTOP MODE ===== -->
    <template v-else>
      <!-- mobile -->
      <source
        v-if="img.mobile"
        :srcset="img.mobile.webp"
        type="image/webp"
        media="(max-width:768px)"
      />

      <source
        v-if="img.mobile"
        :srcset="img.mobile.jpg"
        type="image/jpeg"
        media="(max-width:768px)"
      />

      <!-- desktop -->
      <source
        v-if="img.desktop"
        :srcset="img.desktop.webp"
        type="image/webp"
        media="(min-width:769px)"
      />

      <source
        v-if="img.desktop"
        :srcset="img.desktop.jpg"
        type="image/jpeg"
        media="(min-width:769px)"
      />
    </template>

    <!-- fallback -->
    <img
      :src="img.fallback"
      :srcset="img.jpg_srcset"
      :sizes="sizes"
      :alt="img?.alt ?? ''"
      :loading="loading"
      :class="imgClass"
      width=""
      height=""
    />
  </picture>
</template>
<script>
export default {
  props: {
    img: Object,

    /* optional props */
    sizes: {
      type: String,
      default: '(max-width:768px) 100vw, 50vw',
    },

    loading: {
      type: String,
      default: 'lazy',
    },

    imgClass: {
      type: String,
      default: '',
    },

    wrapperClass: {
      type: String,
      default: '',
    },

    aspectRatio: {
      type: String,
      default: null, // ex: "1/1", "4/5"
    },

    objectFit: {
      type: String,
      default: 'cover',
    },
  },

  computed: {
    finalStyle() {
      const ratio = this.aspectRatio ? this.aspectRatio : `${this.img.width} / ${this.img.height}`;

      return {
        aspectRatio: ratio,
        objectFit: this.objectFit,
      };
    },
  },
  mounted() {
    // console.log('ResponsiveImage IMG →', this.img);
    // console.log('ALT →', this.img?.alt);
  },

  methods: {},
};
</script>

<style>
picture {
  display: block;
  line-height: 0;
}
picture img {
  display: block;
}
</style>
